import { useState } from 'react';
import {
  IconChevronDown,
  IconChevronRight,
  IconLock,
  IconStarFilled,
} from '@tabler/icons-react';
import type { CourseDetail } from '@admitly/shared';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Label } from '../ui/Label.js';
import { ApiError } from '../../lib/api.js';
import {
  useDeleteCourseOverride,
  useUpsertCourseOverride,
} from '../../hooks/useCourseOverride.js';

function readError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

/**
 * Agency-admin editor for the per-tenant overlay on a master course. Collapsed
 * by default. Save upserts the override; Clear (with inline confirm) removes it.
 */
export function AgencyOverlayEditor({ course }: { course: CourseDetail }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(course.agencyNotes ?? '');
  const [commission, setCommission] = useState(course.commissionRateText ?? '');
  const [isPreferred, setIsPreferred] = useState(course.isPreferred);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [saved, setSaved] = useState(false);

  const upsert = useUpsertCourseOverride(course.id);
  const remove = useDeleteCourseOverride(course.id);

  const hasOverride =
    course.isPreferred || !!course.agencyNotes || !!course.commissionRateText;

  function handleSave() {
    setSaved(false);
    upsert.mutate(
      {
        agencyNotes: notes.trim() || null,
        commissionRateText: commission.trim() || null,
        isPreferred,
      },
      { onSuccess: () => setSaved(true) },
    );
  }

  function handleClear() {
    remove.mutate(undefined, {
      onSuccess: () => {
        setNotes('');
        setCommission('');
        setIsPreferred(false);
        setConfirmingClear(false);
        setSaved(false);
      },
    });
  }

  const error = upsert.error ?? remove.error;

  return (
    <section className="surface p-5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        {expanded ? (
          <IconChevronDown size={16} className="text-muted" />
        ) : (
          <IconChevronRight size={16} className="text-muted" />
        )}
        <h2 className="font-serif text-lg text-ink tracking-tightish">Agency overlay</h2>
        {hasOverride && (
          <span className="badge bg-gold/15 text-navy inline-flex items-center gap-1">
            <IconStarFilled size={10} />
            Set
          </span>
        )}
        <span className="ml-auto text-xs text-muted">Private to your agency</span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor="agencyNotes">Agency notes</Label>
            <Textarea
              id="agencyNotes"
              rows={4}
              placeholder="Internal notes your consultants will see on this course…"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setSaved(false);
              }}
            />
          </div>

          <div>
            <Label htmlFor="commissionRateText">Commission rate notes</Label>
            <Input
              id="commissionRateText"
              placeholder="e.g. 18% first year, 12% thereafter"
              value={commission}
              onChange={(e) => {
                setCommission(e.target.value);
                setSaved(false);
              }}
            />
          </div>

          <Checkbox
            checked={isPreferred}
            onChange={(e) => {
              setIsPreferred(e.target.checked);
              setSaved(false);
            }}
            label="Mark as a preferred course (shows a badge to consultants)"
          />

          {saved && !error && (
            <div className="rounded-md bg-success-bg text-success px-3 py-2 text-sm">
              Saved. Your overlay is live for this course.
            </div>
          )}
          {error && (
            <div className="rounded-md bg-danger/10 text-danger border border-danger/20 px-3 py-2 text-sm">
              {readError(error)}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button variant="primary" onClick={handleSave} loading={upsert.isPending}>
              Save overlay
            </Button>

            {confirmingClear ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted">Remove this overlay?</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleClear}
                  loading={remove.isPending}
                >
                  Confirm
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingClear(false)}
                  disabled={remove.isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              hasOverride && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirmingClear(true);
                    setSaved(false);
                  }}
                >
                  Clear
                </Button>
              )
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** Read-only panel shown to consultants when the agency has left notes. */
export function AgencyNotesPanel({ notes }: { notes: string }) {
  return (
    <section className="surface p-5 border-l-2 border-gold bg-cream-deep/30">
      <div className="flex items-center gap-2 mb-2">
        <IconLock size={14} className="text-gold" />
        <h2 className="font-serif text-lg text-ink tracking-tightish">Agency notes</h2>
        <span className="badge bg-cream-deep text-navy text-[10px] uppercase tracking-wider">
          Internal
        </span>
      </div>
      <p className="text-sm text-ink-soft leading-relaxed whitespace-pre-line">{notes}</p>
    </section>
  );
}
